import { useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

import "../styles/assetDetails.css";

import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";

ModuleRegistry.registerModules([AllCommunityModule]);


const getAlignmentStyle = (params) => {
  const value = params?.value;

  // actual numbers
  if (typeof value === "number") {
    return { textAlign: "right" };
  }

  // numeric strings like:
  // "25%", "-10.5", "1,245.88", etc.
  if (typeof value === "string") {
    const numericValue = Number(
      value
        .replace("%", "")
        .replace(/,/g, "")
        .trim()
    );

    const isValidNumber = !isNaN(numericValue);

    if (isValidNumber) {
      return { textAlign: "right" };
    }

    return { textAlign: "left" };
  }

  return null;
};

const mapName = (name) => {
  if (name === "Long/Short Equity") return "Hedged Equities";
  if (name === "Equities") return "Developed Markets Equities";
  return name;
};

const isBlank = (v) => v === null || v === undefined || v === "";

const fmtCur = (v) =>
  isBlank(v)
    ? ""
    : Number(v).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });

const fmtPct = (v) => (isBlank(v) ? "" : `${(Number(v) * 100).toFixed(2)} %`);

const fmtDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}-${d.getFullYear()}`;
};

const AssetDetailsGrid = ({
  data = [],
  selectedStrategy,
  strategySelected, // fallback for old prop name
  accountSelected = false,
}) => {
  const activeStrategy = String(selectedStrategy ?? strategySelected ?? "-1");

  const rows = useMemo(() => {
    return (data || []).map((x) => {
      const strategyName = String(x.strategyName ?? x.StrategyName ?? "").trim();

      const isTitle = !!(x.isTitle ?? x.IsTitle);
      const isStrategy = !!(x.isStrategyLevel ?? x.IsStrategyLevel);
      const isPortfolio = !!(x.isPortfolioLevel ?? x.IsPortfolioLevel);
      const isIndexLevel = !!(x.isIndexLevel ?? x.IsIndexLevel);
      const isWeighted = !!(x.isWeightedIndex ?? x.IsWeightedIndex);
      const isClassLevel = !!(x.isClassLevel ?? x.IsClassLevel);

      let strategyDisplay = mapName(strategyName);
      if (isStrategy) strategyDisplay = `${strategyDisplay} Total`;
      if (isWeighted && strategyName === "Weighted Index") strategyDisplay = "Weighted Index Total";

      return {
        ...x,
        strategyName,
        strategyDisplay,
        isTitle,
        isStrategy,
        isPortfolio,
        isIndexLevel,
        isWeighted,
        isClassLevel,
        accountName: x.accountName ?? x.AccountName ?? "",
        endingBalance: x.endingBalance ?? x.EndingBalance,
        percentage: x.percentage ?? x.Percentage,
        mtd: x.mtd ?? x.MTD,
        ytd: x.ytd ?? x.YTD,
        cumulativeReturnLast36Month:
          x.cumulativeReturnLast36Month ?? x.CumulativeReturnLast36Month,
        cumulativeReturnLast60Month:
          x.cumulativeReturnLast60Month ?? x.CumulativeReturnLast60Month,
        inceptionToDate: x.inceptionToDate ?? x.InceptionToDate,
        classInceptionDate: x.classInceptionDate ?? x.ClassInceptionDate,
        colorCode: x.colorCode ?? x.ColorCode,
      };
    });
  }, [data]);

  // MVC behavior:
  // if strategy != --All--, remove only portfolio total row ("Total")
  const filteredRows = useMemo(() => {
    if (activeStrategy === "-1") return rows;

    return rows.filter((r) => {
      const name = String(r.strategyName || "").trim().toLowerCase();
      const isPortfolioTotal = r.isPortfolio || name === "total";
      return !isPortfolioTotal;
    });
  }, [rows, activeStrategy]);

  const showNumber = (row, value) => {
    if (row.isTitle) return "";
    if (isBlank(value) && value !== 0) return "";
    if (value === 0 && (row.isStrategy || row.isPortfolio || row.isWeighted || row.isIndexLevel)) return "";
    return value;
  };

  const strongStyle = (p) =>
    p.data.isStrategy || p.data.isPortfolio || p.data.isWeighted ? { fontWeight: 700 } : null;

  const columnDefs = useMemo(
    () => [
      {
        headerName: "",
        headerClass: "ag-right-header",
        width: 34,
        height:40,
        sortable: false,
        filter: false,
        resizable: false,
        cellRenderer: (p) => {
          const row = p.data;
          if (!row.isTitle || !row.colorCode) return null;
          return <span className="asset-color-square" style={{ backgroundColor: row.colorCode }} />;
        },
        cellStyle: (params) =>
      {
        const alignmentStyle = getAlignmentStyle(params)
        return params.data?.isTotalPortfolio
          ? { fontWeight: "bold", backgroundColor: "#E6E3F0", ...alignmentStyle }
          : alignmentStyle
      }
      },
      {
        headerName: "",
        headerClass: "ag-right-header",
        field: "strategyDisplay",
        flex: 2.4,
        cellClass: "asset-left",
        cellStyle: (p) =>{
          const alignmentStyle = getAlignmentStyle(p)
          return 
          p.data.isTitle || p.data.isStrategy || p.data.isPortfolio || p.data.isWeighted
            ? { fontWeight: 700, ...alignmentStyle }
            : alignmentStyle
        }
      },
      {
        headerName: accountSelected ? "" : "Account",
        headerClass: "ag-right-header",
        flex: 1.5,
        hide: accountSelected,
        valueGetter: (p) => (p.data.isClassLevel ? p.data.accountName : ""),
        cellStyle: (p)=>{return {...strongStyle(p),...getAlignmentStyle(p)}},
      },
      {
        headerName: "Market Value",
        headerClass: "ag-right-header",
        flex: 1.5,
        type: "rightAligned",
        valueGetter: (p) => fmtCur(showNumber(p.data, p.data.endingBalance)),
        cellStyle: (p)=>{return {...strongStyle(p),...getAlignmentStyle(p)}},
      },
      {
        headerName: "Percentage of Portfolio",
        headerClass: "ag-right-header",
        flex: 2,
        type: "rightAligned",
        valueGetter: (p) => fmtPct(showNumber(p.data, p.data.percentage)),
        cellStyle: (p)=>{return {...strongStyle(p),...getAlignmentStyle(p)}},
      },
      {
        headerName: "Month To Date",
        headerClass: "ag-right-header",
        flex: 1.5,
        type: "rightAligned",
        valueGetter: (p) => fmtPct(showNumber(p.data, p.data.mtd)),
        cellStyle: (p)=>{return {...strongStyle(p),...getAlignmentStyle(p)}},
      },
      {
        headerName: "Year To Date",
        headerClass: "ag-right-header",
        flex: 1.5,
        type: "rightAligned",
        valueGetter: (p) => fmtPct(showNumber(p.data, p.data.ytd)),
        cellStyle: (p)=>{return {...strongStyle(p),...getAlignmentStyle(p)}},
      },
      {
        headerName: "3 Years Annualized",
        headerClass: "ag-right-header",
        flex: 2,
        type: "rightAligned",
        valueGetter: (p) => fmtPct(showNumber(p.data, p.data.cumulativeReturnLast36Month)),
        cellStyle: (p)=>{return {...strongStyle(p),...getAlignmentStyle(p)}},
      },
      {
        headerName: "5 Years Annualized",
        headerClass: "ag-right-header",
        flex: 2,
        type: "rightAligned",
        valueGetter: (p) => fmtPct(showNumber(p.data, p.data.cumulativeReturnLast60Month)),
        cellStyle: (p)=>{return {...strongStyle(p),...getAlignmentStyle(p)}},
      },
      {
        headerName: "Since Inception Annualized",
        headerClass: "ag-right-header",
        flex: 2,
        type: "rightAligned",
        valueGetter: (p) => fmtPct(showNumber(p.data, p.data.inceptionToDate)),
        cellStyle: (p)=>{return {...strongStyle(p),...getAlignmentStyle(p)}},
      },
      {
        headerName: "Inception Date",
        headerClass: "ag-right-header",
        flex: 1.5,
        type: "rightAligned",
        valueGetter: (p) => (p.data.isTitle ? "" : fmtDate(p.data.classInceptionDate)),
        cellStyle: (p)=>{return {...strongStyle(p),...getAlignmentStyle(p)}},
      },
    ],
    [accountSelected]
  );

  return (
    <div className="ag-theme-alpine asset-details-grid">
      <AgGridReact
        theme={"legacy"}
        rowData={filteredRows}
        columnDefs={columnDefs}
        defaultColDef={{ sortable: false, filter: false, resizable: true, wrapHeaderText: true, autoHeaderHeight: true, suppressHeaderMenuButton: true, suppressHeaderContextMenu: true}}
        domLayout="autoHeight"
        headerHeight={64}
        rowHeight={34}
        suppressMovableColumns
        suppressCellFocus
        getRowClass={(p) => {
          if (p.data.isStrategy) return "asset-row-strategy-total";
          if (p.data.isIndexLevel) return "asset-row-index";
          if (p.data.isWeighted) return "asset-row-weighted";
          if (p.data.isTitle) return "asset-row-title";
          return "";
        }}
        getRowStyle={(params) => {
            if (params.data?.isTotalPortfolio) {
              return {
                backgroundColor: "#E6E3F0",
                fontWeight: "bold"
              };
            }

            return {
              backgroundColor: "#FFFFFF"
            };
          }}
        overlayNoRowsTemplate="<span>No Records Available</span>"
      />
    </div>
  );
};

export default AssetDetailsGrid;
